'use client';
import './layout.css';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useMedia } from 'react-use';

import { LandingBuilderProvider } from 'app/_context/LandingBuilderContext';
import MemberProvider from 'app/(authenticated)/(member)/_context/Member/Member';
import { Header } from 'components/theme-v2/header';
import { Sidebar } from 'components/theme-v2/sidebar';
import { RequestNode } from 'generated/types';
import { Sheet, SheetContent } from 'components/ui/sheet';
import { MEMBER_ROUTES, OWNER_ROUTES,} from 'utils/routes';
import { CommunityInfoNode, CurrentUserType } from 'types';
import { useAuth } from 'contexts/auth';

interface ILayoutProps {
  children: React.ReactNode;
  communityInfo?: CommunityInfoNode | null;
  currentUser?: CurrentUserType | null;
  searchRequests: RequestNode[];
}

export const Layout: FC<ILayoutProps> = (props) => {
  const { children, communityInfo, currentUser, searchRequests } = props;

  const pathname = usePathname();
  const { currentUser: clientCurrentUser } = useAuth();
  const isMobile = useMedia('(max-width: 768px)', false);
  const [isOpenMobileMenu, setOpen] = useState<boolean>(false);

  const closeMobileMenu = useCallback(() => {
    setOpen(() => false);
  }, []);

  useEffect(() => {
    setOpen(() => false);
  }, [pathname]);

  const displaySidebar = useMemo(() => {
    const isDiscover = MEMBER_ROUTES.DISCOVERY.resolve(pathname || '');
    return !isDiscover;
  }, [pathname]);

  const sidebarClass = useMemo(() => {
    if (displaySidebar && (currentUser || clientCurrentUser) ) {
      return 'w-[calc(100%-280px)] left-[280px]'
    }
    return 'w-full left-0'
  }, [clientCurrentUser, currentUser, displaySidebar])

  useEffect(() => {
    window.addEventListener('resize', closeMobileMenu);

    return () => {
      window.removeEventListener('resize', closeMobileMenu);
    };
  }, [closeMobileMenu]);

  if (
    MEMBER_ROUTES.WELCOME.resolve(pathname as string) ||
    OWNER_ROUTES.START.resolve(pathname as string) ||
    OWNER_ROUTES.CREATE_COMMUNITY.resolve(pathname as string) ||
    OWNER_ROUTES.SELECT_GOAL.resolve(pathname as string) ||
    OWNER_ROUTES.DESCRIBE.resolve(pathname as string) ||
    MEMBER_ROUTES.EDIT_PROFILE.resolve(pathname as string) ||
    MEMBER_ROUTES.UP.resolve(pathname as string) ||
    MEMBER_ROUTES.ONB_180_BASIC.resolve(pathname as string) ||
    MEMBER_ROUTES.ONB_180_ABOUT.resolve(pathname as string) ||
    MEMBER_ROUTES.ONB_180_PROUD_OF.resolve(pathname as string) ||
    MEMBER_ROUTES.ONB_180_HOBBIES.resolve(pathname as string) ||
    MEMBER_ROUTES.ONB_180_UP_TO.resolve(pathname as string) ||
    MEMBER_ROUTES.ONB_180_DREAMS.resolve(pathname as string)
  ) {
    return <>{children}</>
  }

  if (
    MEMBER_ROUTES.MEMBER_PROFILE.resolve(pathname as string) ||
    MEMBER_ROUTES.MY_PROFILE.resolve(pathname as string)
  ) {
    return <MemberProvider
      communityInfo={communityInfo}
      searchRequests={searchRequests}
      user={currentUser || clientCurrentUser}
    >
      {children}
    </MemberProvider>;
  }

  if (OWNER_ROUTES.LANDING_BUILDER.resolve(pathname as string)) {
    return (
      // TODO: this is hard to understand and should be refactored
      //  better to provider in one page
      //  This had initially been done because it was used over onboarding pages
      //  as well
      <LandingBuilderProvider>
        {children}
      </LandingBuilderProvider>
    );
  }

  return (
    <MemberProvider
      communityInfo={communityInfo}
      searchRequests={searchRequests}
      user={currentUser || clientCurrentUser}
    >
      <div className='w-full fixed top-0 bg-gray-50 z-10'>
        <Header
          logo={communityInfo?.logo}
          onOpenMenu={() => setOpen(() => true)}
          currentUser={currentUser}
        />
      </div>
      {!isMobile && displaySidebar && (currentUser || clientCurrentUser) && (
        <div className='fixed left-0 w-[280px] h-[calc(100%-67px)] top-[67px]'>
          <Sidebar community={communityInfo} currentUser={currentUser || clientCurrentUser} />
        </div>
      )}
      <div
        className={`grid grid-cols-1 flex-1 relative ${sidebarClass} md:w-full md:left-0 top-[67px] bg-gray-50 min-h-[calc(100vh-67px)]`}>
        <div className='py-6 px-8 md:p-3 flex-1 md:relative'>
          {children}
        </div>
      </div>

      {(currentUser || clientCurrentUser) && (
        <Sheet open={isOpenMobileMenu} onOpenChange={() => setOpen(() => false)}>
          <SheetContent
            className='bg-white sm:w-full sm:max-w-[80%] p-0'
            side='left'
          >
            <Sidebar community={communityInfo} currentUser={currentUser || clientCurrentUser} />
          </SheetContent>
        </Sheet>
      )}
    </MemberProvider>
  );
};
